import { useEffect, useRef, useState } from 'react';
import { Loader2, Lock, X } from 'lucide-react';
import {
  fetchChargerPrivateAccess, getDisplayErrorMessage, getErrorCode, searchUsersForRfid, updateChargerPrivateAccess,
  type AdminUserSearchRow, type ChargerPrivateAccess,
} from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { isSiteCallerUser } from '@/lib/siteDashboard';
import {
  PASS_LIST_MAX, addPassMember, canEditPrivateAccess, emptyPrivateWarning, isPrivateDraftDirty,
  passMemberLabel, privateAccessPayload, removePassMember, type PassMember,
} from '@/lib/privateAccess';
import formStyles from '@/components/ui/Form.module.css';
import styles from './ChargerDetailPage.module.css';

/**
 * Private access: the per-charger private switch and its pass list (Warren,
 * 2026-10-22: "Per-charger private switch: same-tenant drivers must pass the
 * pass list too; site host and staff exempt").
 *
 * On, only the drivers on this list, the site host and EVI staff can start
 * the charger, in the app, by RFID card or by autocharge; drivers from other
 * tenants are guests as before. The section reads and writes GET/PATCH
 * /chargers/:id/private-access, gated like the charger edit (Charger:update,
 * EVI staff); a caller without that permission, or a Site, sees the state
 * read-only. Save sends the switch and the list together, with what was
 * loaded, so a charger someone else changed meanwhile is refused (409) and
 * reloaded rather than overwritten. Every saved change is one audit row.
 */
export function PrivateAccessSection({ chargerId, privateAccess, onSaved }: {
  chargerId: string;
  /** The charger read's private_access, shown when the editor is not offered. */
  privateAccess: boolean;
  onSaved: () => void;
}) {
  const user = useAuthStore((s) => s.user);
  const canEdit = canEditPrivateAccess(user, isSiteCallerUser(user));

  const [loaded, setLoaded] = useState<ChargerPrivateAccess | null>(null);
  const [draftPrivate, setDraftPrivate] = useState(false);
  const [draftList, setDraftList] = useState<PassMember[]>([]);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AdminUserSearchRow[]>([]);
  const [searching, setSearching] = useState(false);
  const timer = useRef<number | null>(null);

  const adopt = (state: ChargerPrivateAccess) => {
    setLoaded(state);
    setDraftPrivate(state.private_access);
    setDraftList(state.members);
  };
  const reload = async () => {
    setLoadError('');
    try {
      adopt(await fetchChargerPrivateAccess(chargerId));
    } catch (err) {
      setLoadError(await getDisplayErrorMessage(err, 'Could not load the pass list.'));
    }
  };
  useEffect(() => { if (canEdit) void reload(); }, [chargerId, canEdit]);

  // Debounced driver search (the RFID form's /admin/users/search).
  useEffect(() => {
    if (!canEdit || !query.trim()) { setResults([]); return; }
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      setSearching(true);
      void searchUsersForRfid(query)
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 220);
    return () => { if (timer.current !== null) window.clearTimeout(timer.current); };
  }, [query, canEdit]);

  if (!canEdit) {
    return (
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}><Lock size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />Private access</h2>
        <p className={styles.fieldHint}>
          {privateAccess
            ? 'Private: only the drivers on its pass list, the site host and EVI staff can start this charger.'
            : 'Not private: every driver of this charger\'s network can start it.'}
        </p>
      </div>
    );
  }

  const dirty = loaded ? isPrivateDraftDirty(loaded, draftPrivate, draftList) : false;
  const warning = emptyPrivateWarning(draftPrivate, draftList);
  const onList = new Set(draftList.map((m) => m._id));

  const save = async () => {
    if (!loaded) return;
    setSaving(true);
    setError('');
    try {
      adopt(await updateChargerPrivateAccess(chargerId, privateAccessPayload(loaded, draftPrivate, draftList)));
      setSavedAt(Date.now());
      onSaved();
    } catch (err) {
      const message = await getDisplayErrorMessage(err, 'Save failed');
      setError(message);
      // Changed by someone else since it loaded: show what is there now.
      if (await getErrorCode(err) === 'CONFLICT') await reload();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}><Lock size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />Private access</h2>
      {loadError && <div className={formStyles.formError}>{loadError}</div>}
      {!loaded && !loadError && <p className={styles.fieldHint}><Loader2 size={12} className="spin" /> Loading…</p>}
      {loaded && (
        <>
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 }}>
            <input
              type="checkbox"
              checked={draftPrivate}
              onChange={(e) => setDraftPrivate(e.target.checked)}
              disabled={saving}
              aria-label="Private charger"
            />
            <span>
              <strong>Private charger</strong>
              <span className={styles.fieldHint} style={{ display: 'block' }}>
                Only the drivers on the pass list below, the site host and EVI staff can start it: in the app,
                by RFID card or by autocharge. Drivers in this charger&apos;s own tenant no longer start here
                by being in the tenant. Drivers from other tenants are guests, as before.
              </span>
            </span>
          </label>

          <div className={styles.guestSubcard}>
            <p className={formStyles.label} style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
              Pass list ({draftList.length} / {PASS_LIST_MAX})
            </p>
            {draftList.length === 0 && <p className={styles.fieldHint}>Nobody on the pass list.</p>}
            {draftList.length > 0 && (
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 8px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {draftList.map((m) => (
                  <li key={m._id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{passMemberLabel(m)}</span>
                    <button
                      type="button"
                      onClick={() => setDraftList((l) => removePassMember(l, m._id))}
                      disabled={saving}
                      aria-label={`Remove ${passMemberLabel(m)} from the pass list`}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-tx-m)' }}
                    >
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <input
              className={formStyles.input}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Add a driver: search by name or email"
              aria-label="Search for a driver to add to the pass list"
              disabled={saving || draftList.length >= PASS_LIST_MAX}
              autoComplete="off"
              data-1p-ignore="true"
              data-lpignore="true"
            />
            {searching && <p className={styles.fieldHint}><Loader2 size={12} className="spin" /> Searching…</p>}
            {results.filter((r) => !onList.has(r._id)).length > 0 && (
              <ul style={{ listStyle: 'none', padding: 0, margin: '6px 0 0', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {results.filter((r) => !onList.has(r._id)).map((r) => {
                  const m: PassMember = { _id: r._id, name: r.name ?? r.username ?? null, email: r.email ?? null };
                  return (
                    <li key={r._id}>
                      <button
                        type="button"
                        onClick={() => {
                          const next = addPassMember(draftList, m);
                          if (next.error) { setError(next.error); return; }
                          setError('');
                          setDraftList(next.list);
                          setQuery('');
                          setResults([]);
                        }}
                        style={{ background: 'none', border: '1px solid var(--color-border, #333)', borderRadius: 6, padding: '4px 8px', fontSize: 13, cursor: 'pointer', color: 'inherit', textAlign: 'left', width: '100%' }}
                      >
                        Add {passMemberLabel(m)}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {warning && <p className={styles.fieldHint} role="status" style={{ color: 'var(--evi-power, #facc15)' }}>{warning}</p>}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => { void save(); }}
              disabled={!dirty || saving}
              style={{
                padding: '8px 14px', fontSize: 13,
                background: 'var(--evi-accent, #2ECC71)', color: '#000',
                border: 'none', borderRadius: 8, fontWeight: 600,
                cursor: dirty && !saving ? 'pointer' : 'not-allowed',
                opacity: dirty && !saving ? 1 : 0.5,
              }}
            >
              {saving ? <><Loader2 size={12} className="spin" /> Saving…</> : 'Save private access'}
            </button>
            {dirty && !saving && (
              <button
                type="button"
                onClick={() => { if (loaded) adopt(loaded); setError(''); }}
                style={{ padding: '8px 14px', fontSize: 13, background: 'none', border: '1px solid var(--color-border, #333)', borderRadius: 8, color: 'inherit', cursor: 'pointer' }}
              >
                Undo changes
              </button>
            )}
            {error && <span style={{ color: 'var(--evi-error, #ef4444)', fontSize: 12 }}>{error}</span>}
            {!error && savedAt && !dirty && <span className={styles.fieldHint}>Saved.</span>}
          </div>
        </>
      )}
    </div>
  );
}
